// ProjectFloorPlan.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Context
import { useUser } from 'context/UserContext';

// Utilities
import { countItemsByRoomKey } from 'nodetypes/project/floorplan/floorPlanItemPlacement';
import { printFloorPlan } from 'nodetypes/project/floorplan/floorPlanPrint';
import {
  addFloor,
  addRoom,
  createDefaultFloorPlan,
  createEmptyFloorPlan,
  removeFloor,
  removeRoom,
  renameFloor,
  renameRoom,
} from 'nodetypes/project/floorplan/projectFloorPlanModel';

// Services
import {
  saveProjectFloorPlan,
  subscribeProjectFloorPlan,
} from 'services/projectFloorPlans/ProjectFloorPlanService';

// Hooks
import useProjectFloorPlanItems from 'nodetypes/project/floorplan/useProjectFloorPlanItems';
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';

// CSS
import './ProjectFloorPlan.css';

// Components
import Button from 'components/button/Button';
import Confirm from 'components/confirm/Confirm';
import Divider from 'components/divider/Divider';
import Tabs from 'components/tabs/Tabs';
import FloorPlanRenameFloorModal from 'nodetypes/project/floorplan/components/FloorPlanRenameFloorModal';
import FloorPlanCanvas from 'nodetypes/project/floorplan/FloorPlanCanvas';
import FloorPlanItemsPanel from 'nodetypes/project/floorplan/FloorPlanItemsPanel';

/**
 * ProjectFloorPlan
 *
 * Project-scoped floor plan surface. Always interactive when the user can edit
 * (same always-on pattern as Workflow / Edit elsewhere).
 *
 * @param {object} props.node - Project node
 * @param {boolean} [props.canEdit=true]
 * @returns {ReactNode}
 */
const ProjectFloorPlan = React.memo(({
  node,
  canEdit = true,
}) => {
  // ----- CONTEXT ----- //

  // User Context
  const { user } = useUser();

  // ----- STATE ----- //

  // Layout document
  const [layout, setLayout] = useState(null);

  // Loading / save status
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  // Selection
  const [activeFloorId, setActiveFloorId] = useState(null);
  const [selectedRoomId, setSelectedRoomId] = useState(null);

  // Confirm modal (room / floor delete)
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmLabel, setConfirmLabel] = useState('Delete');

  // Rename floor modal
  const [renameFloorOpen, setRenameFloorOpen] = useState(false);

  // ----- REFS ----- //

  const isSeedingRef = useRef(false);
  const skipRemoteRef = useRef(false);
  const saveTimerRef = useRef(null);
  const layoutRef = useRef(null);
  const confirmActionRef = useRef(null);

  // ----- MEMOS ----- //

  const baseKey = String(node?.baseKey || '').trim();
  const projectKey = String(node?.key || '').trim();
  const isEditing = Boolean(canEdit);
  const floors = Array.isArray(layout?.floors) ? layout.floors : [];

  // ----- HOOKS ----- //

  // Project PO items + placement writers.
  const {
    items,
    isLoading: isItemsLoading,
    assignItemToRoom,
    clearItemPlacement,
    clearPlacementsForRoom,
    renamePlacementsForRoom,
    renamePlacementsForFloor,
  } = useProjectFloorPlanItems({ projectNode: node });

  // Open PO items in docked Peek.
  const { openNode } = useNodeOpenIntent();

  // Item counts keyed by room id.
  const itemCountsByRoomKey = useMemo(
    () => countItemsByRoomKey(items),
    [items],
  );

  // Active floor for rename + print.
  const activeFloor = useMemo(
    () => floors.find((row) => row.id === activeFloorId) || floors[0] || null,
    [activeFloorId, floors],
  );
  const activeFloorName = activeFloor?.name || '';

  // ----- HANDLERS ----- //

  /**
   * persistLayout
   *
   * Writes the layout document to Firestore.
   *
   * @param {object} nextLayout
   * @returns {Promise<object|null>}
   */
  const persistLayout = useCallback(async (nextLayout) => {
    if (!canEdit || !baseKey || !projectKey || !nextLayout) return null;
    setIsSaving(true);
    setSaveError('');
    try {
      const saved = await saveProjectFloorPlan({
        layout: {
          ...nextLayout,
          baseKey,
          projectKey,
        },
        updatedBy: user?.uid || user?.key || null,
      });
      layoutRef.current = saved;
      setLayout(saved);
      return saved;
    } catch (error) {
      console.error('Floor plan save failed', error);
      setSaveError('Could not save floor plan.');
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [baseKey, canEdit, projectKey, user?.key, user?.uid]);

  /**
   * schedulePersist
   *
   * Debounced save after local edits.
   *
   * @param {object} nextLayout
   */
  const schedulePersist = useCallback((nextLayout) => {
    if (!canEdit) return;
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
    }
    saveTimerRef.current = setTimeout(() => {
      persistLayout(nextLayout);
    }, 350);
  }, [canEdit, persistLayout]);

  /**
   * applyLocalLayout
   *
   * Applies a layout update locally and optionally persists.
   *
   * @param {object|Function} nextOrUpdater
   * @param {{ persist?: boolean, immediate?: boolean }} [options]
   */
  const applyLocalLayout = useCallback((nextOrUpdater, options = {}) => {
    const { persist = true, immediate = false } = options;
    setLayout((prev) => {
      const next = typeof nextOrUpdater === 'function'
        ? nextOrUpdater(prev)
        : nextOrUpdater;
      layoutRef.current = next;
      if (persist && next) {
        if (immediate) {
          if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
          persistLayout(next);
        } else {
          schedulePersist(next);
        }
      }
      return next;
    });
  }, [persistLayout, schedulePersist]);

  /**
   * openConfirm
   *
   * Opens Confirm.jsx and stores the action to run on confirm.
   *
   * @param {{ title: string, message: string, confirmLabel?: string, onConfirm: Function }} config
   */
  const openConfirm = useCallback((config) => {
    confirmActionRef.current = config.onConfirm;
    setConfirmTitle(config.title || 'Confirm');
    setConfirmMessage(config.message || '');
    setConfirmLabel(config.confirmLabel || 'Delete');
    setConfirmOpen(true);
  }, []);

  /**
   * handleConfirm
   *
   * Runs the pending confirm action and closes the modal.
   */
  const handleConfirm = useCallback(() => {
    const action = confirmActionRef.current;
    confirmActionRef.current = null;
    setConfirmOpen(false);
    if (typeof action === 'function') action();
  }, []);

  /**
   * executeRemoveRoom
   *
   * Deletes a room and clears its placements.
   *
   * @param {string} roomId
   */
  const executeRemoveRoom = useCallback((roomId) => {
    if (!roomId) return;
    applyLocalLayout((prev) => removeRoom(prev, roomId), { immediate: true });
    setSelectedRoomId(null);
    clearPlacementsForRoom(roomId).catch(() => { });
  }, [applyLocalLayout, clearPlacementsForRoom]);

  /**
   * executeRemoveFloor
   *
   * Deletes a floor, switches selection, and clears room placements.
   *
   * @param {string} floorId
   * @param {string[]} roomIds
   */
  const executeRemoveFloor = useCallback((floorId, roomIds = []) => {
    if (!floorId) return;
    applyLocalLayout((prev) => {
      const next = removeFloor(prev, floorId);
      setActiveFloorId(next.floors[0]?.id || null);
      setSelectedRoomId(null);
      return next;
    }, { immediate: true });

    roomIds.forEach((roomId) => {
      clearPlacementsForRoom(roomId).catch(() => { });
    });
  }, [applyLocalLayout, clearPlacementsForRoom]);

  /**
   * handleRemoveSelectedRoom
   *
   * Removes the selected room; confirms when items are assigned.
   */
  const handleRemoveSelectedRoom = useCallback(() => {
    if (!isEditing || !selectedRoomId) return;

    const roomId = selectedRoomId;
    const assignedCount = itemCountsByRoomKey.get(roomId) || 0;

    if (assignedCount > 0) {
      openConfirm({
        title: 'Delete Room',
        message: `${assignedCount} item${assignedCount === 1 ? '' : 's'} will become unassigned. Delete this room?`,
        confirmLabel: 'Delete Room',
        onConfirm: () => executeRemoveRoom(roomId),
      });
      return;
    }

    executeRemoveRoom(roomId);
  }, [
    executeRemoveRoom,
    isEditing,
    itemCountsByRoomKey,
    openConfirm,
    selectedRoomId,
  ]);

  /**
   * handleRemoveActiveFloor
   *
   * Removes the selected floor when more than one floor exists.
   */
  const handleRemoveActiveFloor = useCallback(() => {
    if (!isEditing || !activeFloorId) return;
    const floorList = Array.isArray(layout?.floors) ? layout.floors : [];
    if (floorList.length <= 1) return;

    const floor = floorList.find((row) => row.id === activeFloorId);
    const roomIds = Array.isArray(floor?.rooms)
      ? floor.rooms.map((room) => room.id).filter(Boolean)
      : [];
    const assignedCount = roomIds.reduce(
      (sum, roomId) => sum + (itemCountsByRoomKey.get(roomId) || 0),
      0,
    );
    const roomCount = roomIds.length;
    const floorName = floor?.name || 'this floor';
    const floorId = activeFloorId;

    let message = `Delete ${floorName}?`;
    if (roomCount > 0 || assignedCount > 0) {
      const parts = [];
      if (roomCount > 0) {
        parts.push(`${roomCount} room${roomCount === 1 ? '' : 's'}`);
      }
      if (assignedCount > 0) {
        parts.push(
          `${assignedCount} item${assignedCount === 1 ? '' : 's'} will become unassigned`,
        );
      }
      message = `Delete ${floorName}? ${parts.join('. ')}.`;
    }

    openConfirm({
      title: 'Delete Floor',
      message,
      confirmLabel: 'Delete Floor',
      onConfirm: () => executeRemoveFloor(floorId, roomIds),
    });
  }, [
    activeFloorId,
    executeRemoveFloor,
    isEditing,
    itemCountsByRoomKey,
    layout,
    openConfirm,
  ]);

  /**
   * handleAddFloor
   *
   * Adds a floor and selects it.
   */
  const handleAddFloor = () => {
    if (!isEditing) return;
    applyLocalLayout((prev) => {
      const next = addFloor(prev);
      setActiveFloorId(next.floors[next.floors.length - 1]?.id || null);
      setSelectedRoomId(null);
      return next;
    }, { immediate: true });
  };

  /**
   * handleAddRoom
   *
   * Adds a room on the active floor.
   */
  const handleAddRoom = () => {
    if (!isEditing) return;
    const floorId = activeFloorId || layout?.floors?.[0]?.id;
    if (!floorId) return;
    applyLocalLayout((prev) => {
      const { layout: next, roomId } = addRoom(prev, floorId, { x: 40, y: 40 });
      if (roomId) setSelectedRoomId(roomId);
      return next;
    }, { immediate: true });
  };

  /**
   * handleLayoutChange
   *
   * Live geometry while dragging — local only until commit.
   *
   * @param {object} nextLayout
   */
  const handleLayoutChange = (nextLayout) => {
    if (!isEditing) return;
    skipRemoteRef.current = true;
    layoutRef.current = nextLayout;
    setLayout(nextLayout);
  };

  /**
   * handleGeometryCommit
   *
   * Persist after room drag/resize ends.
   */
  const handleGeometryCommit = () => {
    if (!isEditing) return;
    skipRemoteRef.current = false;
    const next = layoutRef.current;
    if (next) persistLayout(next);
  };

  /**
   * handleRenameRoom
   *
   * @param {string} roomId
   * @param {string} name
   */
  const handleRenameRoom = (roomId, name) => {
    if (!isEditing) return;
    const nextName = String(name || '').trim();
    const nextLayout = renameRoom(layoutRef.current, roomId, nextName);
    applyLocalLayout(nextLayout, { immediate: true });
    if (nextName) {
      renamePlacementsForRoom(roomId, nextName, nextLayout).catch(() => { });
    }
  };

  /**
   * handleItemDrop
   *
   * @param {string} itemKey
   * @param {object} room
   */
  const handleItemDrop = (itemKey, room) => {
    if (!isEditing || !itemKey || !room?.id) return;
    const item = items.find((row) => row.key === itemKey);
    if (!item) return;
    assignItemToRoom(item, room, layoutRef.current).catch(() => {
      setSaveError('Could not place item.');
    });
  };

  /**
   * handleUnassignDrop
   *
   * @param {string} itemKey
   */
  const handleUnassignDrop = (itemKey) => {
    if (!isEditing || !itemKey) return;
    const item = items.find((row) => row.key === itemKey);
    if (!item) return;
    clearItemPlacement(item).catch(() => {
      setSaveError('Could not unassign item.');
    });
  };

  /**
   * handleFloorTabClick
   *
   * @param {string} floorId
   */
  const handleFloorTabClick = (floorId) => {
    setActiveFloorId(floorId);
    setSelectedRoomId(null);
  };

  /**
   * handleItemClick
   *
   * Opens the PO item in the docked Peek panel.
   *
   * @param {object} item
   */
  const handleItemClick = (item) => {
    if (!item?.key) return;
    openNode(item, { preferPeek: true });
  };

  /**
   * handleRenameActiveFloor
   *
   * Opens the rename floor modal.
   */
  const handleRenameActiveFloor = () => {
    if (!isEditing || !activeFloorId) return;
    setRenameFloorOpen(true);
  };

  /**
   * handlePrintFloors
   *
   * Prints every floor (each floor starts on a new page).
   */
  const handlePrintFloors = () => {
    if (!floors.length) return;
    printFloorPlan({
      projectTitle: node?.title || 'Project',
      floors,
      items,
    });
  };

  /**
   * handleRenameFloorSave
   *
   * Persists the renamed floor from the modal.
   *
   * @param {string} nextName
   */
  const handleRenameFloorSave = (nextName) => {
    if (!isEditing || !activeFloorId) return;
    const trimmed = String(nextName || '').trim();
    const nextLayout = renameFloor(layoutRef.current, activeFloorId, trimmed);
    applyLocalLayout(nextLayout, { immediate: true });
    if (trimmed) {
      renamePlacementsForFloor(activeFloorId, trimmed, nextLayout).catch(() => { });
    }
    setRenameFloorOpen(false);
  };

  // Floor tabs for Tabs.jsx
  const floorTabs = useMemo(
    () => floors.map((floor) => ({
      key: floor.id,
      label: floor.name || 'Floor',
      onClick: () => handleFloorTabClick(floor.id),
    })),
    [floors],
  );

  // ----- EFFECTS ----- //

  // Keep layoutRef aligned with state.
  useEffect(() => {
    layoutRef.current = layout;
  }, [layout]);

  // Clear debounced save on unmount.
  useEffect(() => () => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
  }, []);

  // Subscribe to layout doc; seed Main Floor when missing so every project is ready.
  useEffect(() => {
    if (!baseKey || !projectKey) {
      setLayout(createEmptyFloorPlan({ baseKey, projectKey }));
      setIsLoading(false);
      return undefined;
    }

    setIsLoading(true);
    isSeedingRef.current = false;

    const unsubscribe = subscribeProjectFloorPlan({
      baseKey,
      projectKey,
      onChange: (next) => {
        if (skipRemoteRef.current) {
          setIsLoading(false);
          return;
        }

        const nextFloors = Array.isArray(next?.floors) ? next.floors : [];
        if (nextFloors.length === 0) {
          if (isSeedingRef.current) {
            setIsLoading(false);
            return;
          }

          const seeded = createDefaultFloorPlan({ baseKey, projectKey });
          isSeedingRef.current = true;
          layoutRef.current = seeded;
          setLayout(seeded);
          setActiveFloorId(seeded.floors[0]?.id || null);
          setIsLoading(false);

          if (canEdit) {
            saveProjectFloorPlan({
              layout: seeded,
              updatedBy: user?.uid || user?.key || null,
            }).catch((error) => {
              console.error('Floor plan seed failed', error);
              setSaveError('Could not initialize floor plan.');
            });
          }
          return;
        }

        layoutRef.current = next;
        setLayout(next);
        setIsLoading(false);
        setActiveFloorId((current) => (
          current && nextFloors.some((floor) => floor.id === current)
            ? current
            : (nextFloors[0]?.id || null)
        ));
      },
      onError: (error) => {
        console.error('Floor plan subscribe failed', error);
        const seeded = createDefaultFloorPlan({ baseKey, projectKey });
        layoutRef.current = seeded;
        setLayout(seeded);
        setActiveFloorId(seeded.floors[0]?.id || null);
        setIsLoading(false);
        setSaveError('Could not load floor plan.');
      },
    });

    return unsubscribe;
  }, [baseKey, canEdit, projectKey, user?.key, user?.uid]);

  // Delete selected room / floor with Backspace when editable.
  useEffect(() => {
    if (!isEditing) return undefined;

    /**
     * handleKeyDown
     *
     * @param {KeyboardEvent} event
     */
    const handleKeyDown = (event) => {
      if (event.key !== 'Backspace' && event.key !== 'Delete') return;
      const tag = String(event.target?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || event.target?.isContentEditable) return;
      if (confirmOpen || renameFloorOpen) return;

      if (selectedRoomId) {
        event.preventDefault();
        handleRemoveSelectedRoom();
        return;
      }

      if (activeFloorId && Array.isArray(layout?.floors) && layout.floors.length > 1) {
        event.preventDefault();
        handleRemoveActiveFloor();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [
    activeFloorId,
    confirmOpen,
    handleRemoveActiveFloor,
    handleRemoveSelectedRoom,
    isEditing,
    layout,
    renameFloorOpen,
    selectedRoomId,
  ]);

  // ----- RENDER ----- //

  if (isLoading) {
    return (
      <div className="project-floor-plan-container">
        <div className="project-floor-plan-status">Loading floor plan…</div>
      </div>
    );
  }

  return (
    <div className="project-floor-plan-container">

      {/* FLOOR TABS */}
      <div className="project-floor-plan-floor-tabs">
        {floorTabs.length > 0 ? (
          <Tabs
            tabs={floorTabs}
            selectedKey={activeFloorId || floorTabs[0]?.key}
            showLabels
            layout="wrap"
          />
        ) : (
          <div className="project-floor-plan-floor-tabs-spacer" />
        )}

        {/* FLOOR ACTIONS */}
        <div className="project-floor-plan-floor-actions">
          {isSaving ? (
            <div className="project-floor-plan-status">Saving…</div>
          ) : null}
          {saveError ? (
            <div className="project-floor-plan-status">{saveError}</div>
          ) : null}
          {isEditing ? (
            <>
              <Button
                label="Floor"
                iconKey="add"
                onClick={handleAddFloor}
              />
              <Button
                label="Room"
                iconKey="add"
                enabled={Boolean(activeFloorId || layout?.floors?.length)}
                onClick={handleAddRoom}
              />
              <Divider
                direction="vertical"
                height="24px"
                margin="3px"
              />
              <Button
                label="Rename Floor"
                enabled={Boolean(activeFloorId)}
                onClick={handleRenameActiveFloor}
              />
              <Button
                label="Remove Floor"
                enabled={Boolean(activeFloorId) && floors.length > 1}
                onClick={handleRemoveActiveFloor}
              />
            </>
          ) : (
            <div className="project-floor-plan-status">
              Read only
            </div>
          )}
          <Divider
            direction="vertical"
            height="24px"
            margin="3px"
          />
          <Button
            label="Print"
            iconKey="print"
            enabled={floors.length > 0}
            onClick={handlePrintFloors}
          />
        </div>
      </div>

      {/* BODY */}
      <div className="project-floor-plan-body">

        {/* SIDE */}
        <div className="project-floor-plan-side">
          <FloorPlanItemsPanel
            variant="unassigned"
            items={items}
            layout={layout}
            isEditing={isEditing}
            isLoading={isItemsLoading}
            onItemClick={handleItemClick}
            onUnassignDrop={handleUnassignDrop}
          />
        </div>

        {/* CENTER */}
        <div className="project-floor-plan-center">

          {/* CANVAS WRAPPER */}
          <div className="project-floor-plan-canvas-wrapper">
            <FloorPlanCanvas
              layout={layout}
              activeFloorId={activeFloorId}
              isEditing={isEditing}
              selectedRoomId={selectedRoomId}
              itemCountsByRoomKey={itemCountsByRoomKey}
              showFloorTabs={false}
              onActiveFloorIdChange={setActiveFloorId}
              onGeometryCommit={handleGeometryCommit}
              onItemDrop={handleItemDrop}
              onLayoutChange={handleLayoutChange}
              onRenameRoom={handleRenameRoom}
              onSelectedRoomIdChange={setSelectedRoomId}
            />
          </div>
        </div>

        {/* SIDE */}
        <div className="project-floor-plan-side">
          <FloorPlanItemsPanel
            variant="rooms"
            items={items}
            layout={layout}
            activeFloorId={activeFloorId}
            isEditing={isEditing}
            isLoading={isItemsLoading}
            onItemClick={handleItemClick}
            onRoomDrop={handleItemDrop}
          />
        </div>
      </div>

      <Confirm
        title={confirmTitle}
        message={confirmMessage}
        confirmLabel={confirmLabel}
        secondaryLabel="Cancel"
        isOpen={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={handleConfirm}
        onSecondary={() => setConfirmOpen(false)}
      />

      <FloorPlanRenameFloorModal
        isOpen={renameFloorOpen}
        initialName={activeFloorName}
        onClose={() => setRenameFloorOpen(false)}
        onSave={handleRenameFloorSave}
      />
    </div>
  );
});

// Dev Tools
ProjectFloorPlan.displayName = 'ProjectFloorPlan';

export default ProjectFloorPlan;
